/*
 * CONTACT PAGE
 *
 * Contact detail shell with route-backed tabs, contact actions, and linked account context.
 *
 * Recent changes:
 * - Places header actions below the detail tabs on phones, retaining a shared row from tablet widths.
 * - Allows Contact Overview to extend into the application page scrollbar without clipping.
 * - Uses permission-aware Overview schemas consistently across all Contact entry paths.
 * - Migrated contact detail and My Contacts save state to the shared TanStack Query cache.
 * - Removed entity filter dropdowns from the contact-scoped AI Inbox tab.
 * - Made the detail tab strip horizontally scroll instead of wrapping on narrow screens.
 * - Added an AI Inbox tab scoped to the current contact.
 * - Added a route-synced Tasks tab so the mock kanban prototype is visible on the contact detail page.
 * - Reused the existing account task prototype surface for contact-level UI testing.
 * - Loads the linked account's task board from contact Tasks tabs instead of looking up tasks by contact id.
 * - Uses the shared page rhythm between the contact header and tab body.
 * - Keeps Notes scoped by route contact ID without a redundant task-prefill shortcut.
 */

import { FC, useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router';
import { Contact } from '../models/Contact';
import { getContactPhotoUrl } from '../utils/avatarUtils';
import { getNotesSummaryPrompt } from '../utils/summaryUtils';
import { useNotification } from '../context/NotificationContext';
import ContactDetails from '../components/contact/ContactDetails';
import ContactNotes from '../components/note/AccountNotes';
import { useEventBus } from '../context/EventBusContext';
import { request } from '../services/dataService';
import { API_ENDPOINTS } from '../constants/apiEndpoints';
import { useTeams } from '../context/TeamContext';
import { SchemaProvider } from '../context/SchemaContext';
import Spinner from '../components/common/Spinner';
import { THEME_CLASSES } from '../constants/themeConstants';
import { renderTypeChip } from '../utils/typeChipUtils';
import AccountTasksPrototype from '../components/account/AccountTasksPrototype';
import { AIInboxPanel } from './AIInbox';
import { isAIInboxEnabled } from '../constants/featureFlags';
import PageLayout from '../components/patterns/PageLayout';
import { useAddUserContactMutation, useContactQuery } from '../query/contactQueries';
import { normalizePositiveId } from '../query/queryKeys';

interface ContactPageProps {
  onContactSelect: (contact: Contact) => void;
}

const normalizeContactTab = (tab?: string, noteId?: string) => {
  if (noteId || tab === 'notes') {
    return 'notes';
  }

  if (tab === 'tasks') {
    return 'tasks';
  }

  if (isAIInboxEnabled && tab === 'ai-inbox') {
    return 'ai-inbox';
  }

  return 'overview';
};

const getContactAccountName = (contact: Contact) => {
  return contact.companyName || contact.accountName || 'Linked account';
};

const getLinkedAccountId = (contact: Contact) => {
  const accountId = Number(contact.accountId);
  return Number.isFinite(accountId) && accountId > 0 ? accountId : null;
};

const ContactPageContent: FC<ContactPageProps> = ({ onContactSelect }) => {
  const { showNotification } = useNotification();
  const { currentUserId } = useTeams();
  const { id, tab, noteId } = useParams<{ id: string; tab?: string; noteId?: string }>();
  const navigate = useNavigate();
  const contactId = normalizePositiveId(id);
  const contactQuery = useContactQuery(contactId);
  const addUserContact = useAddUserContactMutation();
  const contact = contactQuery.data ?? null;
  const isLoading = Boolean(contactId && contactQuery.isPending);

  const [activeTab, setActiveTab] = useState<'overview' | 'notes' | 'tasks' | 'ai-inbox'>(() =>
    normalizeContactTab(tab, noteId)
  );

  const { publish } = useEventBus();
  const error = contactQuery.error ? 'Failed to load contact details' : null;

  useEffect(() => {
    setActiveTab(normalizeContactTab(tab, noteId));
  }, [tab, noteId]);

  useEffect(() => {
    if (contact) onContactSelect(contact);
  }, [contact, onContactSelect]);

  const handleSummarize = async () => {
    if (!contact) return;
    const notes = await request(API_ENDPOINTS.GET_CONTACT_NOTES, { id: Number(contact.id) });
    if (!notes || !notes.length) return;
    const prompt = getNotesSummaryPrompt(notes);
    publish('chat:open', prompt);
  };

  if (isLoading) return (
    <div className="fixed inset-0 flex items-center justify-center">
      <Spinner />
    </div>
  );

  if (error) return (
    <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">{error}</div>
      </div>
    </div>
  );

  if (!contact) return (
    <div className={`p-6 ${THEME_CLASSES.text.primary}`}>
      <div className="flex items-center justify-center h-64">
        <div>Contact not found</div>
      </div>
    </div>
  );

  const linkedAccountId = getLinkedAccountId(contact);

  return (
    <PageLayout>
      <div className={`${THEME_CLASSES.background}`}>
        <div className="mx-auto">
          <div className="px-4">
            <div className="mb-4 flex flex-row items-start gap-4">
              <div className="w-20 h-20 rounded-lg border-2 ${THEME_CLASSES.border} overflow-hidden flex-shrink-0">
                <img
                  src={getContactPhotoUrl(contact)}
                  alt={`${contact.firstName} ${contact.lastName}`}
                  className="w-full h-full object-cover bg-gray-300 dark:bg-gray-600"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = '';
                    target.className = 'w-full h-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center';
                  }}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4">
                  <div className="flex-1 flex flex-col md:flex-row gap-2 md:gap-4 items-start md:items-center">
                    <h1 className={`text-lg font-semibold my-auto ${THEME_CLASSES.text.primary}`}>
                      {contact.firstName} {contact.lastName}
                    </h1>

                    <div className="flex items-center gap-2">
                      <Link
                        to={`/accounts/${contact.accountId?.toString()}`}
                        className={`rounded-full py-1 px-3 text-xs md:text-sm font-medium ${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`}
                      >
                        {contact.companyName || contact.accountName}
                      </Link>
                      {renderTypeChip({ rlpCompanyId: contact.rlpCompanyId, teamId: contact.teamId })}
                    </div>
                  </div>
                  <div className="flex gap-2 md:gap-4">
                    <div className={`text-sm ${THEME_CLASSES.text.secondary} truncate`}>
                      {contact.email}
                    </div>
                    <div className={`text-sm ${THEME_CLASSES.text.secondary} truncate`}>
                      {contact.phone}
                    </div>
                  </div>
                </div>
                <div className="mt-4 -ml-24 flex flex-col items-stretch gap-2 md:ml-0 md:flex-row md:items-center md:justify-between">
                  <div className="w-full min-w-0 overflow-x-auto pb-1 md:w-auto md:flex-1">
                    <nav className="flex min-w-max flex-nowrap gap-2" aria-label="Tabs">
                      <button
                        onClick={() => {
                          setActiveTab('overview');
                          navigate(`/contacts/${id}/overview`);
                        }}
                        className={`shrink-0 whitespace-nowrap rounded-full py-1 px-3 text-xs md:text-sm font-medium ${activeTab === 'overview'
                          ? `${THEME_CLASSES.button.primary} font-bold`
                          : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                          }`}
                      >
                        Overview
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('notes');
                          if (noteId) {
                            navigate(`/contacts/${id}/notes/${noteId}`);
                          } else {
                            navigate(`/contacts/${id}/notes`);
                          }
                        }}
                        className={`shrink-0 whitespace-nowrap rounded-full py-1 px-3 text-xs md:text-sm font-medium ${activeTab === 'notes'
                          ? `${THEME_CLASSES.button.primary} font-bold`
                          : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                          }`}
                      >
                        Contacts and Notes
                      </button>
                      <button
                        onClick={() => {
                          setActiveTab('tasks');
                          navigate(`/contacts/${id}/tasks`);
                        }}
                        className={`shrink-0 whitespace-nowrap rounded-full py-1 px-3 text-xs md:text-sm font-medium ${activeTab === 'tasks'
                          ? `${THEME_CLASSES.button.primary} font-bold`
                          : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                          }`}
                      >
                        Tasks
                      </button>
                      {isAIInboxEnabled && (
                        <button
                          onClick={() => {
                            setActiveTab('ai-inbox');
                            navigate(`/contacts/${id}/ai-inbox`);
                          }}
                          className={`shrink-0 whitespace-nowrap rounded-full py-1 px-3 text-xs md:text-sm font-medium ${activeTab === 'ai-inbox'
                            ? `${THEME_CLASSES.button.primary} font-bold`
                            : `${THEME_CLASSES.button.secondary} ${THEME_CLASSES.hover.background}`
                            }`}
                        >
                          AI Inbox
                        </button>
                      )}
                    </nav>
                  </div>


                  <div className="flex shrink-0 items-center gap-2">
                    <div className="flex gap-2">
                      <button
                        onClick={async () => {
                          try {

                            if (!currentUserId) {
                              showNotification('User ID not found', 'error');
                              return;
                            }
                            await addUserContact.mutateAsync({
                              userId: currentUserId,
                              id: Number(contact.id)
                            });
                            showNotification(`${contact.firstName} ${contact.lastName} saved to your contact list!`, 'success');
                          } catch (error) {
                            console.error('Error saving contact:', error);
                            showNotification('Failed to save contact', 'error');
                          }
                        }}
                        className={`rounded ${THEME_CLASSES.hover.background}`}
                        title="Save to your contacts"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 ${THEME_CLASSES.text.primary}`} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                        </svg>
                      </button>

                      <button
                        onClick={handleSummarize}
                        className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                        title="Ask AI about this contact"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </button>

                      <button
                        onClick={() => {
                          const eventTitle = `RE: ${contact.firstName} ${contact.lastName}`;
                          const calendarUrl = `https://calendar.google.com/calendar/u/0/r/eventedit?text=${encodeURIComponent(eventTitle)}`;
                          window.open(calendarUrl, '_blank');
                        }}
                        className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                        title="Add to Google Calendar"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </button>

                      <button
                        onClick={() => {
                          const subject = `RE: ${contact.firstName} ${contact.lastName}`;
                          const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&su=${encodeURIComponent(subject)}`;
                          window.open(gmailUrl, '_blank');
                        }}
                        className={`p-2 rounded-md ${THEME_CLASSES.text.secondary} ${THEME_CLASSES.hover.background}`}
                        title="Compose Gmail"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className={`h-px w-full ${THEME_CLASSES.border}`}></div>
          </div>
        </div>
      </div>
      <div
        data-contact-tab-content="true"
        className={`flex-1 min-h-0 ${activeTab === 'overview' ? '' : 'overflow-hidden'}`}
      >
        <div className="h-full min-h-0">
          {activeTab === 'overview' && (
            <ContactDetails contact={contact} />
          )}
          {activeTab === 'notes' && contact && (
            <ContactNotes
              accountId={contact.accountId || 0}
              accountName={contact.companyName || contact.accountName || ''}
              initialContactId={contact.id}
              initialNoteId={noteId ? parseInt(noteId) : undefined}
              accountTeamId={contact?.teamId}
            />
          )}
          {activeTab === 'tasks' && contact && (
            linkedAccountId ? (
              <AccountTasksPrototype
                accountId={linkedAccountId}
                accountName={getContactAccountName(contact)}
              />
            ) : (
              <div className="flex h-full min-h-[26rem] items-center justify-center">
                <div className={`max-w-lg rounded-3xl border ${THEME_CLASSES.card.border} ${THEME_CLASSES.card.background} px-8 py-10 text-center shadow-sm`}>
                  <h2 className={`text-lg font-semibold ${THEME_CLASSES.text.primary}`}>No linked account for tasks</h2>
                  <p className={`mt-3 text-sm leading-6 ${THEME_CLASSES.text.secondary}`}>
                    Account-level tasks will appear here after this contact is linked to an account.
                  </p>
                </div>
              </div>
            )
          )}
          {isAIInboxEnabled && activeTab === 'ai-inbox' && contact && (
            <AIInboxPanel
              fixedContactId={Number(contact.id)}
              showAccountFilter={false}
              showContactFilter={false}
            />
          )}
        </div>
      </div>
    </PageLayout>
  );
};

const ContactPage: FC<ContactPageProps> = (props) => {
  return (
    <SchemaProvider>
      <ContactPageContent {...props} />
    </SchemaProvider>
  );
};

export default ContactPage;
